//AIModelConfig.tsx
import React, { useState, useEffect, useRef } from 'react'
import { aiModelService, AIModel, isModelDisabledInLanguage } from './utils/aiModelService'
import { Tag } from 'antd';

export type { AIModel }

export interface OrderedModel {
  model: AIModel
  tier: 'Best' | 'Better' | 'Good' | 'None'
}

export const findPreferredOrderedModel = (
  orderedModels: OrderedModel[],
  preferredModelId?: string
): OrderedModel | null => {
  const target = (preferredModelId || '').trim()
  if (!target) return null
  return orderedModels.find(({ model }) => model.id === target) || null
}

export const useAIModels = (
  language?: string,
  isLangTesterUser: boolean = false
): { models: AIModel[]; loading: boolean } => {
  const [models, setModels] = useState<AIModel[]>([])
  const [loading, setLoading] = useState<boolean>(true)

  useEffect(() => {
    const loadModels = async () => {
      setLoading(true)
      try {
        const activeModels = await aiModelService.getActiveModels(isLangTesterUser, true)
        setModels(activeModels)
      } catch (error) {
        console.error('Error loading AI models:', error)
      } finally {
        setLoading(false)
      }
    }

    loadModels()
  }, [isLangTesterUser])

  return { models, loading }
}

const getNormalizedDeprecationDate = (model: AIModel): string | null => {
  const raw = (model.deprecationDate || model.depreciationDate || '').trim()
  if (!raw) return null
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null
  const parsed = new Date(`${raw}T00:00:00Z`)
  if (Number.isNaN(parsed.getTime())) return null
  if (parsed.toISOString().slice(0, 10) !== raw) return null
  return raw
}

const getDeprecationTagLabel = (dateISO: string, todayISO: string): string => {
  if (dateISO <= todayISO) {
    return `Deprecated ${dateISO}`
  }
  return `Deprecates ${dateISO}`
}

export const renderModelOption = (model: AIModel): React.ReactNode => {
  // Visibility is authoritative; cached custom tags may predate a public rollout.
  const displayTags = model.tags?.filter(tag =>
    model.internalOnly || tag.trim().toLowerCase() !== 'internal',
  )
  const todayISO = new Date().toISOString().slice(0, 10)
  const deprecationDateISO = getNormalizedDeprecationDate(model)
  const deprecationTagLabel = deprecationDateISO
    ? getDeprecationTagLabel(deprecationDateISO, todayISO)
    : null
  const isAlreadyDeprecated = !!(deprecationDateISO && deprecationDateISO <= todayISO)

  return (
    <span>
      {model.displayName}
      {/* Render custom tags first */}
      {displayTags?.map(tag => (
        <Tag
          key={tag}
          color="purple"
          style={{
            marginLeft: 4,
            position: 'relative',
            top: '-0.5px',
            fontSize: '0.75em'
          }}
        >
          {tag}
        </Tag>
      ))}
      {deprecationTagLabel && (
        <Tag
          color={isAlreadyDeprecated ? 'red' : 'volcano'}
          style={{ marginLeft: 8, position: 'relative', top: '-0.5px' }}
        >
          {deprecationTagLabel}
        </Tag>
      )}
      {/* Then render discount/premium pricing tags */}
      {model.discount != null && model.discount > 0 && (
        <Tag
          color="green"
          style={{ marginLeft: 8, position: 'relative', top: '-0.5px' }}
        >
          {model.discount}% Off
        </Tag>
      )}
      {model.premium != null && model.premium > 0 && (
        <Tag
          color="volcano"
          style={{ marginLeft: 8, position: 'relative', top: '-0.5px' }}
        >
          +{model.premium}% Premium
        </Tag>
      )}
      {model.isFree && (
        <Tag
          color="cyan"
          style={{ marginLeft: 8, position: 'relative', top: '-0.5px' }}
        >
          Free
        </Tag>
      )}
      {model.allowsNSFW && (
        <Tag
          color="red"
          style={{ marginLeft: 8, position: 'relative', top: '-0.5px' }}
        >
          Allows NSFW
        </Tag>
      )}
      {model.testingOnly && (
        <Tag
          color="orange"
          style={{ marginLeft: 8, position: 'relative', top: '-0.5px' }}
        >
          Testing
        </Tag>
      )}
    </span>
  )
}

export const useOrderedModels = (
  language?: string,
  isLangTesterUser: boolean = false,
  sourceLanguage?: string
): { orderedModels: OrderedModel[]; loading: boolean } => {
  const [orderedModels, setOrderedModels] = useState<OrderedModel[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const abortControllerRef = useRef<AbortController | null>(null)
  const debugModels = (() => {
    try {
      return localStorage.getItem('debugProofreadModal') === 'true'
    } catch (_e) {
      return false
    }
  })()
  const debugStamp = (label: string) => {
    if (!debugModels) return
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now()
    console.log(`[AIModelConfig] ${label} @ ${Math.round(now)}ms`)
  }

  useEffect(() => {
    // 1) if language is empty, clear out and do not fetch
    if (!language) {
      setOrderedModels([])
      setLoading(false)
      return
    }

    debugStamp(`useOrderedModels:start lang=${language} source=${sourceLanguage || ''}`)
    // 2) cancel any in-flight request
    abortControllerRef.current?.abort()
    const abortController = new AbortController()
    abortControllerRef.current = abortController

    const loadModels = async () => {
      setLoading(true)

      try {
        // fetch all active
        let allModels = await aiModelService.getActiveModels(isLangTesterUser, true)
        if (abortController.signal.aborted) return

        // normalize
        const lang = language.trim()
        const source = sourceLanguage?.trim()

        const hasCompleteDisplayOrder = allModels.every(
          model => Number.isFinite(model.displayOrder)
        )
        if (hasCompleteDisplayOrder) {
          const tierValues = await Promise.all(
            allModels.map(model =>
              aiModelService.getTierForModel(model.id, lang, source)
            )
          )
          if (abortController.signal.aborted) return
          const combined: OrderedModel[] = allModels.map((model, index) => ({
            model,
            // Tier remains available for legacy default-model selection.
            tier: tierValues[index],
          }))
          combined.sort((a, b) => {
            const orderDelta = (a.model.displayOrder || 0) - (b.model.displayOrder || 0)
            if (orderDelta !== 0) return orderDelta
            return a.model.displayName.localeCompare(b.model.displayName)
          })
          setOrderedModels(combined)
          debugStamp(`useOrderedModels:loaded displayOrder models=${combined.length}`)
          return
        }

        // Compatibility fallback until every active model has displayOrder.
        const [best, better, good] = await Promise.all([
          aiModelService.getModelsByTier('Best', lang, isLangTesterUser, source),
          aiModelService.getModelsByTier('Better', lang, isLangTesterUser, source),
          aiModelService.getModelsByTier('Good', lang, isLangTesterUser, source),
        ])
        if (abortController.signal.aborted) return

        type Tier = 'Best' | 'Better' | 'Good' | 'None'

        // map of each model’s default
        const defaultTierMap = new Map<string, Tier>(
          allModels.map(m => [m.id, (m.defaultTier as Tier) || 'None'])
        )

        // filter out any overrides that match the default
        const bestOverrideIds = new Set(
          best.filter(m => defaultTierMap.get(m.id) !== 'Best').map(m => m.id)
        )
        const betterOverrideIds = new Set(
          better.filter(m => defaultTierMap.get(m.id) !== 'Better').map(m => m.id)
        )
        const goodOverrideIds = new Set(
          good.filter(m => defaultTierMap.get(m.id) !== 'Good').map(m => m.id)
        )

        // start tierMap with each model’s default
        const tierMap = new Map<string, Tier>()
        allModels.forEach(m => {
          tierMap.set(m.id, defaultTierMap.get(m.id)!)
        })

        // apply overrides in strict precedence
        bestOverrideIds.forEach(id => tierMap.set(id, 'Best'))
        betterOverrideIds.forEach(id => tierMap.set(id, 'Better'))
        goodOverrideIds.forEach(id => tierMap.set(id, 'Good'))

        // build and sort
        const combined: OrderedModel[] = allModels.map(m => ({
          model: m,
          tier: tierMap.get(m.id)!,
        }))

        const order: Record<Tier, number> = {
          Best: 0,
          Better: 1,
          Good: 2,
          None: 3,
        }
        combined.sort((a, b) => order[a.tier] - order[b.tier])

        setOrderedModels(combined)
        debugStamp(`useOrderedModels:loaded models=${combined.length}`)
      } catch (err: any) {
        if (err.name !== 'AbortError') {
          console.error('Error loading ordered models:', err)
        }
      } finally {
        setLoading(false)
        debugStamp('useOrderedModels:end')
      }
    }

    loadModels()

    return () => {
      abortController.abort()
    }
  }, [language, sourceLanguage, isLangTesterUser])

  return { orderedModels, loading }
}
